// WHAT: React Context for managing favorite navigation items
// WHY:  Provides global state management for favorite items with localStorage persistence
// DOMAIN: Shared → Contexts
// DEPENDENCIES: React Context API, localStorage
// RISKS:
//   - NOTE: localStorage may not be available in SSR environments
//   - NOTE: No validation of favorite IDs - may store invalid values
//   - NOTE: localStorage sync happens on every favorites change - may cause performance issues

import { useEffect, useState, type ReactNode } from "react";

import { FavoritesContext } from "./FavoritesContext.shared";

/**
 * LocalStorage key for persisting favorites
 */
const FAVORITES_KEY = "nav:favorites";

/**
 * WHAT:
 *   Provider component that manages favorite navigation items with localStorage persistence.
 *
 * WHY:
 *   Provides a centralized way to manage favorites across the application with
 *   automatic persistence to localStorage.
 *
 * BEHAVIOR:
 *   - Initializes favorites from localStorage on mount
 *   - Persists favorites to localStorage on every change
 *   - Provides toggleFavorite function to add/remove favorites
 *   - Handles SSR by checking localStorage availability
 *
 * RISKS:
 *   - NOTE: localStorage may not be available in SSR environments
 *   - NOTE: No validation of favorite IDs - may store invalid values
 *   - NOTE: localStorage sync happens on every favorites change - may cause performance issues
 *
 * @param root0 - Props object
 *
 * @param root0.children - Child components
 *
 * @returns FavoritesProvider component
 *
 */
export const FavoritesProvider = ({ children }: { children: ReactNode }) => {
  const [favorites, setFavorites] = useState<string[]>(() => {
    if (typeof localStorage === "undefined") {
      return [];
    }
    const stored = localStorage.getItem(FAVORITES_KEY);
    return stored ? (JSON.parse(stored) as string[]) : [];
  });

  useEffect(() => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  }, [favorites]);

  const toggleFavorite = (id: string) => {
    setFavorites((previous) =>
      previous.includes(id) ? previous.filter((f) => f !== id) : [...previous, id],
    );
  };

  return (
    <FavoritesContext.Provider value={{ favorites, toggleFavorite }}>
      {children}
    </FavoritesContext.Provider>
  );
};
